import React, { useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { GoAlertFill } from "react-icons/go";
import { UserContext } from "../context/UserContext";
import Spinner from "../Assets/auth_spinner.svg";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import "../styles/LoginPage.css";

function SignUpPage() {
  const { setIsVerified, setVerificationEmail } = useContext(UserContext);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    nationality: "",
    cnic: "",
    password: "",
    confirm_password: "",
    phone_number: "+92 ",
    email: "",
  });

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    nationality: "",
    cnic: "",
    password: "",
    confirm_password: "",
    phone_number: "",
    email: "",
  });

  // Regex for password: at least 8 characters, one uppercase, one number, one special character
  const passwordRegex =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]).{8,}$/;
  // CNIC: exactly 13 digits
  const cnicRegex = /^\d{13}$/;
  // Phone: should match "+92 " followed by exactly 10 digits
  const phoneRegex = /^\+92\s\d{10}$/;

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({
    first_name: false,
    last_name: false,
    nationality: false,
    cnic: false,
    password: false,
    confirm_password: false,
    phone_number: false,
    email: false,
  });

  useGSAP(() => {
    gsap.from(".floating-label", {
      duration: 0.8,
      y: 50,
      opacity: 0,
      stagger: 0.1,
      ease: "power2.out",
    });

    gsap.from("#submitBtn", {
      duration: 1,
      scale: 0.5,
      opacity: 0,
      delay: 0.3,
      ease: "power2.out",
    });
  }, []);

  const validateField = (name, value) => {
    let error = "";
    if (!value.trim()) {
      error = "Required";
    } else {
      if (name === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error = "Invalid email format";
      } else if (name === "phone_number" && !phoneRegex.test(value)) {
        error = "Invalid phone number. Format: +92 followed by 10 digits";
      } else if (name === "cnic" && !cnicRegex.test(value)) {
        error = "CNIC must consist of exactly 13 digits";
      } else if (name === "password" && !passwordRegex.test(value)) {
        error =
          "Password must be at least 8 characters long, contain at least one uppercase letter, one number, and one special character.";
      } else if (name === "confirm_password" && value !== formData.password) {
        error = "Confirm password should match the original password";
      }
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Only validate if the user has typed something
    if (value.trim() !== "") {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }

    // Special check for password and confirm_password match
    if (
      (name === "password" || name === "confirm_password") &&
      formData.password &&
      formData.confirm_password
    ) {
      setErrors((prev) => ({
        ...prev,
        confirm_password:
          formData.password !== formData.confirm_password
            ? "Passwords must match"
            : "",
      }));
    }
  };

  const handleFocus = (field) => {
    // Clear error when field gets focus
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "phone_number") {
      // Remove all non-digit characters, then limit to 10 digits (after +92)
      const digits = newValue.replace(/\D/g, "").slice(2, 12); // skip first two digits "92"
      newValue = "+92 " + digits;
    } else if (name === "cnic") {
      // Remove non-digit characters and limit to 13 digits
      newValue = newValue.replace(/\D/g, "").slice(0, 13);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSignupError(null);

    // Validate all fields
    const newErrors = {};
    let hasError = false;

    Object.entries(formData).forEach(([name, value]) => {
      newErrors[name] = validateField(name, value);
      if (newErrors[name]) hasError = true;
    });

    setErrors(newErrors);
    setTouched({
      first_name: true,
      last_name: true,
      nationality: true,
      cnic: true,
      password: true,
      confirm_password: true,
      phone_number: true,
      email: true,
    });

    if (hasError) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/signup`,
        formData
      );

      if (response?.data?.success) {
        setIsVerified(false);
        setVerificationEmail(formData.email);
        navigate("/verify-otp");
        return;
      }
    } catch (error) {
      setSignupError(
        error.response?.data?.message ||
          "Unexpected error occurred. Please try again"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="grid grid-cols-2 w-full h-screen">
        {/* Left Side */}
        <div
          className="h-screen relative bg-cover bg-center bg-no-repeat flex items-center justify-center"
          style={{ backgroundImage: "url('/Assets/11.jpg')" }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <h1 className="text-4xl font-bold text-white z-10">
            Welcome To SocraticAli Consultants
          </h1>
        </div>

        {/* Right Side - Form */}
        <div className="flex items-center justify-center h-screen pr-10">
          <div className="p-8 bg-white shadow-lg rounded-lg h-auto w-[70%] mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-2 text-start">
              Sign Up
            </h1>
            <p className="text-sm text-gray-500 mb-6 text-start">
              SocraticAli Consultant Portal
            </p>

            <form onSubmit={handleSignup} className="space-y-4 w-full">
              {/* First Name and Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <div className="floating-label">
                  <input
                    type="text"
                    name="first_name"
                    placeholder=" "
                    className={`focus:ring-1 focus:ring-[#457B9D] ${
                      touched.first_name && errors.first_name
                        ? "input-error"
                        : ""
                    }`}
                    value={formData.first_name}
                    onChange={handleChange}
                    onFocus={() => handleFocus("first_name")}
                  />
                  <label htmlFor="first_name">First Name</label>
                  <p
                    className={`helper-text ${
                      touched.first_name && errors.first_name ? "hidden" : ""
                    }`}
                  >
                    Please enter First Name
                  </p>
                  {touched.first_name && errors.first_name && (
                    <p className="error-message visible">{errors.first_name}</p>
                  )}
                </div>

                <div className="floating-label">
                  <input
                    type="text"
                    name="last_name"
                    placeholder=" "
                    className={`focus:ring-1 focus:ring-[#457B9D] ${
                      touched.last_name && errors.last_name ? "input-error" : ""
                    }`}
                    value={formData.last_name}
                    onChange={handleChange}
                    onFocus={() => handleFocus("last_name")}
                  />
                  <label htmlFor="last_name">Last Name</label>
                  <p
                    className={`helper-text ${
                      touched.last_name && errors.last_name ? "hidden" : ""
                    }`}
                  >
                    Please enter Last Name
                  </p>
                  {touched.last_name && errors.last_name && (
                    <p className="error-message visible">{errors.last_name}</p>
                  )}
                </div>
              </div>

              {/* Nationality and CNIC */}
              <div className="grid grid-cols-2 gap-4">
                <div className="floating-label">
                  <input
                    type="text"
                    name="nationality"
                    placeholder=" "
                    className={`focus:ring-1 focus:ring-[#457B9D] ${
                      touched.nationality && errors.nationality
                        ? "input-error"
                        : ""
                    }`}
                    value={formData.nationality}
                    onChange={handleChange}
                    onFocus={() => handleFocus("nationality")}
                  />
                  <label htmlFor="nationality">Nationality</label>
                  <p
                    className={`helper-text ${
                      touched.nationality && errors.nationality ? "hidden" : ""
                    }`}
                  >
                    Please enter your Nationality
                  </p>
                  {touched.nationality && errors.nationality && (
                    <p className="error-message visible">
                      {errors.nationality}
                    </p>
                  )}
                </div>

                <div className="floating-label">
                  <input
                    type="text"
                    name="cnic"
                    placeholder=" "
                    className={`focus:ring-1 focus:ring-[#457B9D] ${
                      touched.cnic && errors.cnic ? "input-error" : ""
                    }`}
                    value={formData.cnic}
                    onChange={handleChange}
                    onFocus={() => handleFocus("cnic")}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="cnic">CNIC</label>
                  <p
                    className={`helper-text ${
                      touched.cnic && errors.cnic ? "hidden" : ""
                    }`}
                  >
                    Please Enter CNIC without dashes e.g: 4123456222222
                  </p>
                  {touched.cnic && errors.cnic && (
                    <p className="error-message visible">{errors.cnic}</p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className="floating-label relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder=" "
                  className={`focus:ring-1 focus:ring-[#457B9D] pr-10 ${
                    touched.password && errors.password ? "input-error" : ""
                  }`}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus("password")}
                />
                <label htmlFor="password">Password</label>
                <div
                  className="absolute right-3 top-3 cursor-pointer text-gray-600"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </div>
                <p
                  className={`helper-text ${
                    touched.password && errors.password ? "hidden" : ""
                  }`}
                >
                  Password must be at least 8 characters long, contain at least
                  one uppercase letter, one number, and one special character.
                </p>
                {touched.password && errors.password && (
                  <p className="error-message visible">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="floating-label relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  placeholder=" "
                  className={`focus:ring-1 focus:ring-[#457B9D] pr-10 ${
                    touched.confirm_password && errors.confirm_password
                      ? "input-error"
                      : ""
                  }`}
                  value={formData.confirm_password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => handleFocus("confirm_password")}
                />
                <label htmlFor="confirm_password">Confirm Password</label>
                <div
                  className="absolute right-3 top-3 cursor-pointer text-gray-600"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </div>
                <p
                  className={`helper-text ${
                    touched.confirm_password && errors.confirm_password
                      ? "hidden"
                      : ""
                  }`}
                >
                  Please re-enter your password to confirm.
                </p>
                {touched.confirm_password && errors.confirm_password && (
                  <p className="error-message visible">
                    {errors.confirm_password}
                  </p>
                )}
              </div>

              {/* Phone and Email */}
              <div className="grid grid-cols-2 gap-4">
                <div className="floating-label">
                  <input
                    type="text"
                    name="phone_number"
                    placeholder=" "
                    className={`focus:ring-1 focus:ring-[#457B9D] ${
                      touched.phone_number && errors.phone_number
                        ? "input-error"
                        : ""
                    }`}
                    value={formData.phone_number}
                    onChange={handleChange}
                    onFocus={() => handleFocus("phone_number")}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="phone_number">Primary Cell Phone</label>
                  <p
                    className={`helper-text ${
                      touched.phone_number && errors.phone_number
                        ? "hidden"
                        : ""
                    }`}
                  >
                    Please enter your phone number in the format: +92 3XXXXXXXXX
                  </p>
                  {touched.phone_number && errors.phone_number && (
                    <p className="error-message visible">
                      {errors.phone_number}
                    </p>
                  )}
                </div>

                <div className="floating-label">
                  <input
                    type="email"
                    name="email"
                    placeholder=" "
                    className={`focus:ring-1 focus:ring-[#457B9D] ${
                      touched.email && errors.email ? "input-error" : ""
                    }`}
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus("email")}
                  />
                  <label htmlFor="email">Primary Email</label>
                  <p
                    className={`helper-text ${
                      touched.email && errors.email ? "hidden" : ""
                    }`}
                  >
                    Please enter your email
                  </p>
                  {touched.email && errors.email && (
                    <p className="error-message visible">{errors.email}</p>
                  )}
                </div>
              </div>

              {signupError && (
                <div className="flex items-center gap-x-2 bg-red-100 border border-red-700 text-red-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
                  <GoAlertFill className="size-5" />
                  <p>{signupError}.</p>
                </div>
              )}

              <button
                type="submit"
                id="submitBtn"
                className="w-full bg-[#1E3557] text-white px-4 py-2 rounded-md font-semibold relative"
              >
                Create Account
                {loading && (
                  <img
                    src={Spinner}
                    alt=""
                    className="size-6 absolute top-2 right-5"
                  />
                )}
              </button>
            </form>

            {/* Links */}
            <div className="flex justify-center text-sm text-gray-500 mt-4">
              <span>
                Already Have An Account?{" "}
                <button
                  onClick={() => navigate("/signin")}
                  className="text-[#457B9D] font-semibold"
                >
                  Sign In
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
